// The grounding footnotes shown beneath an insight — how much of the data stands behind the picture.
//
// Pure sentence builders, in ONE module because these lines must read identically wherever they appear:
// the fill rate is a property of the COLUMN, so the same column captions a pill list and a slider with
// the same words, and a second copy of the wording is a second copy that can drift. `InsightPanel` is
// the only renderer — it already decides which control to draw, so it is the one place that can caption
// any of them without each control growing its own version.
//
// Every builder returns `undefined` rather than a placeholder when the measurement is absent: absent
// stays absent, the rule the counts already follow. An unmeasured fill rate is not a fill rate of zero,
// and an all-blank column is a real and different claim.

import { isFiniteNumber } from '@/_shared/lib/numeric';

import { formatPercent } from './format';

/**
 * Whether a control's denominator must be disclosed as an estimate: soft unless its accuracy label reads
 * exactly `exact`. An UNRECOGNISED label and a MISSING one both count as soft, matching the worst-first
 * ranking the row builder applies to these same strings — an unknown provenance is not evidence of
 * precision. Shared so every control that publishes the same row count discloses it the same way; the
 * row builder emits one qualifier precisely so the screen cannot show two answers for one number.
 */
export function isTotalEstimated(total: number | undefined, totalAccuracy: string | undefined): boolean {
  return total !== undefined && totalAccuracy !== 'exact';
}

/**
 * The fill-rate line: what share of rows have a value for this column at all.
 *
 * `quotesNonNullShare` is for the controls whose readout is a share of the NON-NULL rows rather than of
 * every row — the quantile slider, and the histogram whose `total` is its own `nonNull`. When the fill
 * rate is unknown, staying silent would leave that percentage looking like a share of everyone, so the
 * unknown case gets a sentence of its own. It must be passed FALSE while no threshold is committed:
 * there is no percentage on screen to qualify then, and the disclaimer would sit under a control reading
 * "No threshold set", explaining a number nobody can see. Every other control has nothing to disclaim.
 */
export function fillRateLine(nonNullFraction: number | undefined, quotesNonNullShare: boolean): string | undefined {
  if (isFiniteNumber(nonNullFraction)) {
    // `formatPercent`, not a bare round: a 99.8%-filled column printing "100% of rows have a value"
    // states there are no blanks when there are, and a 0.4%-filled one printing "0%" states the opposite.
    return `${formatPercent(nonNullFraction * 100)} of rows have a value for this column`;
  }
  return quotesNonNullShare ? 'Fill rate unknown — the share is of rows that have a value' : undefined;
}

/**
 * The pill list's own line: whether the values on screen ARE the value domain or a slice of it.
 *
 * The pill list is capped at the probe's top-k (10 by default) while a column stays pill-eligible up to
 * 50 distinct values, so a list is often a slice presented as a menu — and on a column where the values
 * shown are everything, saying so is just as useful, because it tells the reader the enumeration is safe.
 *
 * `values` is the number of distinct VALUES the pills stand for, NOT the number of pills. Folding renders
 * two spellings as one chip carrying a "2 spellings" badge, so eight chips can represent ten values. The
 * count belongs to the values because that is what the sentence makes its claim about: "all of them are
 * shown" is a statement about the column's domain, not about how many chips it took to draw it.
 *
 * Completeness is tested EXACTLY, never by rounding. 0.9984 rounds to 100% but is a real 0.16% of rows
 * sitting in values that are NOT on screen, and calling that "all shown" asserts an enumeration is safe
 * when it is not — the precise failure this line exists to prevent. {@link formatPercent} carries the
 * matching guard on the other side, printing `>99%` rather than letting a near-miss read as a flat 100%,
 * so the two halves can never contradict each other.
 */
export function valueCoverageLine(valueCoverage: number | undefined, values: number): string | undefined {
  if (!isFiniteNumber(valueCoverage) || values <= 0) return undefined;
  // A tolerance for the float sum of per-value fractions ONLY — deliberately far too tight to admit any
  // real gap. It exists so a genuinely complete list summing to 0.9999999999999999 still reads complete.
  if (valueCoverage >= 1 - 1e-9) {
    return values === 1 ? 'This is the only value in this column' : `All ${values} values are shown`;
  }
  const pct = formatPercent(valueCoverage * 100);
  return values === 1
    ? `This value covers ${pct} of rows with a value`
    : `These ${values} values cover ${pct} of rows with a value`;
}
